import { useMemo, useState } from 'react';
import { MARGIN_PRODUCTS, MARGIN_CURRENCIES, QUOTE_RATES } from '@/mocks/margin';

export type PriceMode = 'auto' | 'manual';

export function useMarginCalculator() {
  const [product, setProduct] = useState('EURUSD');
  const [lots, setLots] = useState(1);
  const [leverage, setLeverage] = useState('500');
  const [currency, setCurrency] = useState('USD');
  const [priceMode, setPriceMode] = useState<PriceMode>('auto');
  const [manualPrice, setManualPrice] = useState(1.0845);

  const prod = useMemo(
    () => MARGIN_PRODUCTS.find((p) => p.value === product) ?? MARGIN_PRODUCTS[0],
    [product],
  );
  const curr = useMemo(
    () => MARGIN_CURRENCIES.find((c) => c.value === currency) ?? MARGIN_CURRENCIES[0],
    [currency],
  );

  const leverageNum = parseInt(leverage, 10) || 1;
  const price = priceMode === 'auto' ? prod.price : manualPrice;

  // Contract value in quote currency, then converted to account currency.
  const contractValueQuote = lots * prod.contractSize * price;
  const marginQuote = contractValueQuote / leverageNum;
  const quoteRate = QUOTE_RATES[prod.quote] ?? 1;
  const marginAccount = (marginQuote / quoteRate) * curr.rate;
  const contractValueAccount = (contractValueQuote / quoteRate) * curr.rate;

  const lotsInvalid = Number.isNaN(lots) || lots <= 0;
  const priceInvalid = priceMode === 'manual' && (Number.isNaN(manualPrice) || manualPrice <= 0);
  const hasError = lotsInvalid || priceInvalid;

  const leveragePosition = Math.min(Math.max((leverageNum - 50) / (1000 - 50), 0), 1) * 100;
  const leverageLevel = leverageNum <= 100 ? 'Thấp' : leverageNum <= 500 ? 'Trung bình' : 'Cao';

  return {
    product,
    setProduct,
    lots,
    setLots,
    leverage,
    setLeverage,
    currency,
    setCurrency,
    priceMode,
    setPriceMode,
    manualPrice,
    setManualPrice,
    prod,
    curr,
    price,
    leverageNum,
    contractValueAccount,
    marginAccount,
    lotsInvalid,
    priceInvalid,
    hasError,
    leveragePosition,
    leverageLevel,
  };
}

export type MarginCalculatorState = ReturnType<typeof useMarginCalculator>;